/**
 * rarityUtils.js — Client-side rarity display utilities
 * 
 * Provides color mapping, CSS class helpers, and a fallback rarity
 * calculator for items that don't have a rarity property yet
 * (e.g., items in the DB from before the rarity system).
 */

// ============================================================
// RARITY TIERS & COLORS
// ============================================================
export const RARITY_TIERS = {
  common:    { label: 'Common',    color: '#9d9d9d', glow: 'none',                                    order: 0 },
  uncommon:  { label: 'Uncommon',  color: '#1eff00', glow: '0 0 6px rgba(30, 255, 0, 0.3)',           order: 1 },
  rare:      { label: 'Rare',      color: '#0070dd', glow: '0 0 6px rgba(0, 112, 221, 0.4)',          order: 2 },
  epic:      { label: 'Epic',      color: '#a335ee', glow: '0 0 8px rgba(163, 53, 238, 0.4)',         order: 3 },
  legendary: { label: 'Legendary', color: '#ff8000', glow: '0 0 10px rgba(255, 128, 0, 0.5)',         order: 4 },
  mythic:    { label: 'Mythic',    color: '#e6cc80', glow: '0 0 12px rgba(230, 204, 128, 0.6)',       order: 5 },
};

/**
 * Get the rarity color for an item.
 * Falls back to common gray if rarity is missing.
 */
export function getRarityColor(item) {
  if (!item) return RARITY_TIERS.common.color;
  const rarity = item.rarity || inferRarity(item);
  return RARITY_TIERS[rarity]?.color || RARITY_TIERS.common.color;
}

/**
 * Get the rarity label for an item.
 */
export function getRarityLabel(item) {
  if (!item) return 'Common';
  const rarity = item.rarity || inferRarity(item);
  return RARITY_TIERS[rarity]?.label || 'Common';
}

/**
 * Get the rarity CSS class name for an item.
 * Returns a class like "rarity-common", "rarity-epic", etc.
 */
export function getRarityClass(item) {
  if (!item) return 'rarity-common';
  const rarity = item.rarity || inferRarity(item);
  return `rarity-${rarity}`;
}

/**
 * Get the text-shadow glow for a rarity tier.
 */
export function getRarityGlow(item) {
  if (!item) return 'none';
  const rarity = item.rarity || inferRarity(item);
  return RARITY_TIERS[rarity]?.glow || 'none';
}

// ============================================================
// FALLBACK RARITY INFERENCE
// ============================================================

// Equipment types that can have enchantments
const EQUIPMENT_TYPES = ['weapon', 'breastplate', 'helm', 'gloves', 'grieves', 'pauldrons', 'belt', 'armor', 'ring', 'shield'];

/**
 * Infer rarity for items that don't have the rarity property.
 * This is a client-side fallback for old items in the DB.
 * Mirrors the server-side ensureRarity logic.
 */
export function inferRarity(item) {
  if (!item) return 'common';
  if (item.rarity) return item.rarity;

  const type = item.type;
  const cost = item.cost || 0;
  // Handle both array and object-format magic (quest rewards use {0:{...}} format)
  const magicArr = Array.isArray(item.magic) ? item.magic
    : (item.magic && typeof item.magic === 'object') ? Object.values(item.magic) : [];
  const hasMagic = magicArr.length > 0;
  const hasMdamage = !!item.mdamage;
  const hasEnemy = !!item.enemy;

  // Consumables, parts, misc — common
  if (['restore', 'parts', 'misc', 'inn', 'recipe_scroll'].includes(type)) {
    return 'common';
  }

  // Scrolls
  if (type === 'scroll') return 'uncommon';

  // Tomes by cost
  if (type === 'tome') {
    if (cost >= 2500) return 'rare';
    if (cost >= 800) return 'uncommon';
    return 'common';
  }

  // Gems
  if (type === 'gem') {
    if (cost >= 200) return 'uncommon';
    return 'common';
  }

  // Backpacks
  if (type === 'backpack') {
    if (cost >= 5000) return 'rare';
    if (cost >= 1000) return 'uncommon';
    return 'common';
  }

  // Mounts
  if (type === 'mount') return 'uncommon';

  // Crafted items — use quality
  if (item.crafted && item.quality) {
    switch (item.quality) {
      case 'Masterwork': return hasMagic ? 'epic' : 'rare';
      case 'Superior':   return 'rare';
      case 'Fine':       return 'uncommon';
      default:           return 'common';
    }
  }

  // Equipment — mirrors server-side ensureRarity / assignBaseRarity logic
  if (EQUIPMENT_TYPES.includes(type)) {
    // Items with magic enchantments (treasure-generated) — use totalMod like server
    if (hasMagic && !item.crafted) {
      const totalMod = magicArr.reduce((sum, m) => sum + (m.mod || 0), 0)
        + (item.att || 0) * 5;
      if (totalMod > 700) return 'mythic';
      if (totalMod > 500) return 'legendary';
      if (totalMod > 200) return 'epic';
      if (totalMod > 50)  return 'rare';
      if (magicArr.length >= 2) return 'rare';
      if (magicArr.length >= 1) return 'uncommon';
    }

    // Base rarity (no magic) — uses cost + special property thresholds
    if (cost >= 10000) {
      if (hasMdamage || hasEnemy) return 'legendary';
      return 'epic';
    }
    if (cost >= 3000) {
      if (hasMdamage || hasEnemy) return 'epic';
      return 'rare';
    }
    if (cost >= 500) {
      if (hasMdamage || hasEnemy) return 'rare';
      return 'uncommon';
    }
    if (cost >= 100) {
      if (hasMdamage || hasEnemy) return 'uncommon';
      return 'common';
    }
    // Low-cost items with special properties still get a bump
    if (hasMdamage || hasEnemy) return 'uncommon';
  }

  return 'common';
}
